/**
 * A uuid shown short without losing anything: its 128 bits in base62, 22 characters instead of 36.
 * Reversible, so a value copied off the screen decodes back to the uuid a filter needs — which a
 * truncated prefix cannot do, and which no amount of compression can beat: a v4 uuid is 122 bits of
 * randomness, so ~20 characters is the floor for any lossless form.
 *
 * Base62 rather than base64url because it is the same 22 characters wide but has no `-` or `_` in
 * its alphabet, so the value reads as one word and survives double-click selection.
 */
const ALPHABET = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
const LENGTH = 22;

export const shortenUuid = (uuid?: string): string => {
  const hex = (uuid ?? '').replace(/-/g, '');
  if (!/^[0-9a-f]{32}$/i.test(hex)) return uuid ?? '';
  let value = BigInt(`0x${hex}`);
  let short = '';
  while (value > 0n) {
    short = ALPHABET[Number(value % 62n)] + short;
    value /= 62n;
  }
  // Padded so the width never varies and leading zero bits survive the round trip.
  return short.padStart(LENGTH, '0');
};

export const expandUuid = (short?: string): string | undefined => {
  if (short?.length !== LENGTH) return undefined;
  let value = 0n;
  for (const char of short) {
    const digit = ALPHABET.indexOf(char);
    if (digit < 0) return undefined;
    value = value * 62n + BigInt(digit);
  }
  const hex = value.toString(16).padStart(32, '0');
  if (hex.length !== 32) return undefined;
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};
